import { Box, Stack } from '@mui/material'
import React from 'react'
import { useFieldArray, useWatch } from 'react-hook-form'
import { ScrollRestore } from 'src/Libs/flow/components'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { setIsEvseExpandedToCache } from 'src/Libs/flow/parsers'
import { Eebus, EmcInfoForEebus, ExpandCollapseButtons, ModbusTcp, RfidCardsSection } from './components'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'

/** Overview of the wallbox charging-stations with Eebus- and Modbus-Entries */
export const ChargingStations: React.FC = () => {
  return (
    <>
      <BannerQueue location={BannerLocations.FlowSettingsAdvancedSettingsChargingStation} sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: [2, 0], mb: { xs: 8, sm: 0 } }}>
        <Entries type="eebus" />
        <Entries type="modbusTcp" />
        <ScrollRestore />
      </Box>
    </>
  )
}

const Entries: React.FC<{ type: 'eebus' | 'modbusTcp' }> = ({ type }) => {
  const { control, setValue, getValues } = useInstallationForm()
  const installationId = useWatch({ control, name: 'id' })

  const { fields } = useFieldArray({
    control,
    name: `chargingStations.${type}`,
  })

  const evesExpandedState = useWatch({
    control,
    name: `chargingStationsExpandedState.${type}`,
  })

  const expandAll = (): void => {
    const evse = getValues(`chargingStations.${type}`)
    for (const evseDevice of evse) {
      setIsEvseExpandedToCache(evseDevice.deviceId, true)
    }
    for (const evseId of Object.keys(evesExpandedState)) {
      setValue(`chargingStationsExpandedState.${type}.${evseId}`, true)
    }
  }

  const collapseAll = (): void => {
    const evse = getValues(`chargingStations.${type}`)
    for (const evseDevice of evse) {
      setIsEvseExpandedToCache(evseDevice.deviceId, false)
    }
    for (const evseId of Object.keys(evesExpandedState)) {
      setValue(`chargingStationsExpandedState.${type}.${evseId}`, false)
    }
  }

  const expandedValues = Object.values(evesExpandedState)
  const areAllExpanded = !!expandedValues.length && expandedValues.every(expanded => !!expanded)
  const areAllCollapsed = !!expandedValues.length && expandedValues.every(expanded => !expanded)

  if (!fields.length) return null

  return (
    <Stack gap={1} mb={{ xs: 2, sm: 2, md: 1 }}>
      {type === 'eebus' && <EmcInfoForEebus />}
      {type === 'modbusTcp' && installationId !== null && <RfidCardsSection installationId={installationId} />}

      <ExpandCollapseButtons
        disableCollapseAll={areAllCollapsed}
        disableExpandAll={areAllExpanded}
        onCollapseClick={collapseAll}
        onExpandClick={expandAll}
        align="start"
      />
      <Stack gap={1}>
        {fields.map((item, index) => (
          <React.Fragment key={item.id}>
            {type === 'eebus' && <Eebus index={index} />}
            {type === 'modbusTcp' && <ModbusTcp index={index} />}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  )
}
